import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { User } from 'app/shared/models/user';
import { IApplicationWithPermissions, RoleService } from 'app/shared/services/role.service';
import { UserFacade } from 'app/shared/services/facades/user.facade';
import { catchError, takeUntil, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { Role } from 'app/shared/models/role';
import {
  AuthService,
  Destroy,
  DialogResult,
  DialogService,
  SecureActionDialogData,
  sort,
  tr,
  ViewMode,
} from '@kpd/framework-ngx';
import { Permissions } from 'app/shared/services/permissions';

export interface IApplicationWithRoles {
  name: string;
  roles: IUserRole[];
}

export interface IUserRole {
  readOnly: boolean;
  loading: boolean;
  hasRole: boolean;
  role: string;
  error?: string;
}

@Component({
  selector: 'app-user-details-roles',
  templateUrl: './user-details-roles.component.html',
})
export class UserDetailsRolesComponent implements OnInit, OnChanges, OnDestroy {
  Permissions: typeof Permissions = Permissions;
  @Input()
  item: User;
  @Output()
  switchMode = new EventEmitter<string>();
  @Output()
  itemChanged = new EventEmitter<number>();

  @Destroy()
  destroy$: any;
  loading = false;
  applicationsWithRoles: IApplicationWithRoles[];
  readOnlyMode = true;
  isRoleToggled = false;

  get hasSystemUserManagePermission(): boolean {
    return this.authService.getIdentity().hasPermission(Permissions.SystemUserManage);
  }

  get isSystemUser(): boolean {
    return this.item.isSystemUser;
  }

  constructor(
    private roleService: RoleService,
    private userFacade: UserFacade,
    private route: ActivatedRoute,
    private authService: AuthService,
    private dialogService: DialogService,
  ) {
    this.userFacade.loading$
      .pipe(takeUntil(this.destroy$))
      .subscribe((load) => (this.loading = load['detailLoading'] && load['detailLoading'].length > 0));
  }

  ngOnInit(): void {
    if (!this.item.upn) {
      this.loading = true;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.item && !!changes.item.currentValue && !!changes.item.currentValue.key) {
      this.applicationsWithRoles = [];
      this.readOnlyMode = true;
      if (
        this.route.snapshot.queryParams['mode'] === ViewMode.Edit &&
        this.authService.getIdentity().hasPermission(Permissions.UserRoleUpdate) &&
        (!this.isSystemUser || this.hasSystemUserManagePermission)
      ) {
        this.readOnlyMode = false;
      }
      this.getRoles(true);
    }
  }

  private getRoles(reload: boolean = false): void {
    if (reload) {
      this.isRoleToggled = false;
      this.userFacade.reloadUser(this.item).subscribe((user: User) => {
        this.item = user;
        this.getRoles();
      });
      return;
    }
    this.userFacade.detailLoadingStarted();
    if (this.readOnlyMode) {
      this.getUserRoles();
    } else {
      this.createRoles();
    }
  }

  createRoles(): void {
    const activeTenantId = this.authService.getTenant().key.id;
    this.roleService
      .getAppsWithRoles()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: IApplicationWithPermissions[]) => {
        const newAppsWithRoles: IApplicationWithRoles[] = result.map((app) => {
          app.roles = sort(app.roles, { name: 'name' }) as any;

          const mappedRoles = app.roles.map((role) => ({
            role: role.name,
            hasRole: false,
            loading: false,
            readOnly: false,
          }));

          return {
            name: app.name,
            roles: mappedRoles,
          };
        });
        this.item.getApplicationsWithRoles(activeTenantId).forEach((applicationWithRoles) => {
          const matchingApp = newAppsWithRoles.find(
            (app: IApplicationWithRoles) => app.name === applicationWithRoles.name,
          );
          if (!matchingApp) {
            newAppsWithRoles.push({
              name: applicationWithRoles.name,
              roles: applicationWithRoles.roles.map((appRole) => {
                return { role: appRole.name, readOnly: true, loading: false, hasRole: true };
              }),
            });

            return;
          }
          applicationWithRoles.roles.forEach((appRole: Role) => {
            const foundRole = matchingApp.roles.find((someRole) => someRole.role === appRole.name);
            if (!foundRole) {
              matchingApp.roles.push({ role: appRole.name, readOnly: true, loading: false, hasRole: true });
              return;
            }
            foundRole.hasRole = true;
          });
        });

        newAppsWithRoles.sort((a, b) => a.name.localeCompare(b.name));
        this.applicationsWithRoles = newAppsWithRoles;
        this.userFacade.detailLoadingFinished();
      });
  }

  getUserRoles(): void {
    console.log('getUserRoles');
    if (!this.item.getApplicationsWithRoles) {
      return;
    }
    const activeTenantId = this.authService.getTenant().key.id;
    this.applicationsWithRoles = this.item.getApplicationsWithRoles(activeTenantId).map((roleApp) => {
      roleApp.roles.sort((a, b) => a.name.localeCompare(b.name));
      const mappedRoles = roleApp.roles.map((role) => ({
        role: role.name,
        hasRole: false,
        loading: false,
        readOnly: false,
      }));
      return {
        name: roleApp.name,
        roles: mappedRoles,
      };
    });
    this.userFacade.detailLoadingFinished();
  }

  onToggle(value: IUserRole, applicationName: string): void {
    if (value.loading) {
      return;
    }
    value.loading = true;

    if (this.isSystemUser && !value.hasRole) {
      const messages = [
        {
          text: tr('Are you sure you want to remove this role from a System User?'),
        },
      ];
      const confirmData: SecureActionDialogData = {
        header: tr(`Remove {1} from {2} ?`)
          .replace('{1}', `${tr('Role')} ${value.role}`)
          .replace('{2}', ` ${this.item.displayName}`),
        messages: messages,
        cancelButtonText: tr('Cancel'),
        verifyButtonText: tr('Remove'),
        verifyButtonStyle: 'danger',
        verificationString: value.role.trim(),
        verificationLabel: tr('Type the name of the {value} to confirm').replace(/{.*}/, `${tr('role')}`),
      };
      this.dialogService
        .open('secureDelete', confirmData)
        .afterClosed()
        .subscribe((result: DialogResult) => {
          if (result === DialogResult.Verify) {
            this.executeToggleRole(applicationName, value);
            return true;
          } else {
            value.hasRole = true;
            value.loading = false;
            return false;
          }
        });
    } else {
      this.executeToggleRole(applicationName, value);
    }
  }

  private executeToggleRole(applicationName: string, value: IUserRole) {
    this.userFacade
      .toggleRole(this.item.key, applicationName, value.role, value.hasRole)
      .pipe(
        tap(() => {
          value.error = undefined;
        }),
        catchError((error) => {
          if (error.status !== 409 && error.status !== 404) {
            value.hasRole = !value.hasRole;
            value.error = tr(
              'Something went wrong saving this role, please refresh your page to see if your changes are saved!',
            );
          }
          return of(!value.hasRole);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.isRoleToggled = true;
        let total = 0;
        this.applicationsWithRoles.forEach((app) => {
          total += app.roles.filter((role) => role.hasRole).length;
        });
        this.itemChanged.emit(total);
        value.loading = false;
      });
  }

  toggleViewMode(): void {
    this.readOnlyMode = !this.readOnlyMode;
    // If a role is toggled, the user must be reloaded when going to consult mode
    this.getRoles(this.isRoleToggled);
    this.switchMode.emit(this.readOnlyMode ? ViewMode.Consult : ViewMode.Edit);
  }

  ngOnDestroy(): void {
    // Needed for custom destroy decorator
  }
}
